import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useAdapterContext } from "../use-adapter-context/index.mjs";
import { sortEventOccurrences } from "../sort-event-occurrences/index.mjs";

/**
 * Places event occurrences for a list of days, where if an event is rendered in a day, it fills the entire day cell (no notion of time).
 */
export function useEventOccurrencesWithDayGridPosition(parameters) {
  const {
    days,
    occurrencesMap,
    shouldAddPosition
  } = parameters;
  const adapter = useAdapterContext();
  return React.useMemo(() => {
    const indexLookup = {};
    const dayListSize = days.length;
    const processedDays = days.map((day, dayIndex) => {
      indexLookup[day.key] = {
        occurrencesIndex: {},
        usedIndexes: new Set()
      };
      const needsPosition = [];
      const withoutPosition = [];

      // 1. Split occurrences into withPosition and withoutPosition
      for (const occurrence of occurrencesMap.get(day.key) ?? []) {
        const hasPosition = shouldAddPosition ? shouldAddPosition(occurrence) : true;
        if (hasPosition) {
          needsPosition.push(occurrence);
        } else {
          withoutPosition.push(occurrence);
        }
      }

      // 2. Sort the withPosition occurrences by start and end date
      const sortedNeedsPosition = sortEventOccurrences(needsPosition);

      // 3. Assign position to each occurrence
      const withPosition = [];
      for (const occurrence of sortedNeedsPosition) {
        let position;
        const occurrenceIndexInPreviousDay = dayIndex === 0 ? null : indexLookup[days[dayIndex - 1].key].occurrencesIndex[occurrence.key];

        // If the event is present in the previous day, we keep the same index
        if (occurrenceIndexInPreviousDay != null) {
          position = {
            index: occurrenceIndexInPreviousDay,
            daySpan: 1,
            isInvisible: true
          };
        }
        // Otherwise, we find the smallest available index
        else {
          const usedIndexes = indexLookup[day.key].usedIndexes;
          let i = 1;
          while (usedIndexes.has(i)) {
            i += 1;
          }
          const durationInDays = adapter.differenceInDays(occurrence.displayTimezone.end.value, day.value) + 1;
          position = {
            index: i,
            daySpan: Math.min(durationInDays, dayListSize - dayIndex) // Don't go past the day list end
          };
        }
        indexLookup[day.key].occurrencesIndex[occurrence.key] = position.index;
        indexLookup[day.key].usedIndexes.add(position.index);
        withPosition.push(_extends({}, occurrence, {
          position
        }));
      }

      // Sort the occurrences by their index to make sure they are in the order they should be rendered in.
      withPosition.sort((a, b) => a.position.index - b.position.index);
      return _extends({}, day, {
        withPosition,
        withoutPosition
      });
    });
    const usedIndexes = Object.values(indexLookup).flatMap(day => Array.from(day.usedIndexes));
    return {
      days: processedDays,
      maxIndex: usedIndexes.length === 0 ? 1 : Math.max(...usedIndexes)
    };
  }, [adapter, days, occurrencesMap, shouldAddPosition]);
}